import { Component, OnInit, OnDestroy } from '@angular/core';
import { EnforcementService, CitationAdjustmentType, ApplyCitationAdjustmentRequest, CitationAdjustmentResponse } from 'src/app/Services/enforcement.service';
import { SimpleModalComponent } from '@looorent/ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, Validators, AbstractControl, ValidationErrors, FormGroup, FormControl, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-addcitationadjustment',
    templateUrl: './addadjustment.component.html',
    styleUrls: ['./addadjustment.component.scss'],
    standalone: false
})
export class AddCitationAdjustmentComponent extends SimpleModalComponent<{ CitationId: number; CitationPrice: number }, CitationAdjustmentResponse | undefined> implements OnInit, OnDestroy {

  constructor(
    public enforcementService: EnforcementService,
    private toastr: ToastrService,
    private formBuilder: FormBuilder
  ) {
    super();
  }

  private readonly subs = new Subscription();

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      isSetPriceMode: [false],
      amount: [0, [this.citationAdjustmentAmountValidator(this.CitationPrice)]],
      reason: ['', [Validators.required, Validators.maxLength(512), this.nonEmptyTrimmed]],
      notes: [null as string | null, [Validators.maxLength(2048)]],
    });

    this.subs.add(
      this.form.get('isSetPriceMode')!.valueChanges.subscribe(() => {
        this.form.get('amount')!.updateValueAndValidity({ emitEvent: false });
      })
    );
    this.form.get('amount')?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  form!: FormGroup<{
    isSetPriceMode: FormControl<boolean | null>;
    amount: FormControl<number | null>;
    reason: FormControl<string | null>;
    notes: FormControl<string | null>;
  }>;

  CitationId!: number;
  CitationPrice!: number; // start number
  IsSetPriceMode: boolean = false;

  nonEmptyTrimmed(c: AbstractControl): ValidationErrors | null {
    if (typeof c.value === 'string' && c.value.trim() === '') {
      return { required: true };
    } else {
      return null;
    }
  }

  citationAdjustmentAmountValidator(CitationPrice: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control.parent as FormGroup | null;
      const isSetPriceMode = !!group?.get('isSetPriceMode')?.value;
      const name = isSetPriceMode ? "Updated price" : "Discount amount";

      const raw = control.value;
      if (raw === null || raw === undefined || raw === '') {
        return { amount: `${name} is required.` };
      }

      const amount = Number(raw);

      if (Number.isNaN(amount)) {
        return { amount: `${name} is invalid.` };
      }

      if (amount < 0) {
        return { amount: `${name} must be positive.` };
      }

      if (CitationPrice === undefined || CitationPrice === null) {
        return null;
      }

      if (isSetPriceMode) {
        if (amount > CitationPrice) {
          return {
            amount: "Updated price cannot be greater than the current price."
          };
        }
        if (amount === CitationPrice) {
          return {
            amount: "Updated price must be different from the current price."
          };
        }
      } else {
        if (amount === 0) {
          return {
            amount: "Discount amount must be greater than 0."
          };
        }
        if (amount > CitationPrice) {
          return {
            amount: "Total discount cannot be greater than the current price."
          };
        }
      }
      return null;
    }
  }


  isSubmitting: boolean = false;
  async confirm(): Promise<void> {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;

    const { isSetPriceMode, amount, reason, notes } = this.form.getRawValue();

    if (amount === null || reason === null) {
      this.form.markAllAsTouched();
      this.isSubmitting = false;
      return;
    }

    let model: ApplyCitationAdjustmentRequest = {
      Amount: amount,
      AdjustmentType: CitationAdjustmentType.Discount,
      Reason: reason,
      Notes: notes
    };

    if (!isSetPriceMode) {
      try {
        this.result = await this.enforcementService.ApplyCitationAdjustment(this.CitationId, model);
        this.close();
      } catch (error) {
        this.toastr.error('Failed to apply adjustment.');
      }
      this.isSubmitting = false;
      return;

    }

    model.Amount = this.CitationPrice - amount;

    if (model.Amount <= 0) {
      console.warn('Computed adjustment amount is <= 0, should have been caught by validation');
      this.toastr.error('Computed adjustment amount is invalid.');
      this.isSubmitting = false;
      return;
    }

    try {
      this.result = await this.enforcementService.ApplyCitationAdjustment(this.CitationId, model);
      this.close();
    } catch (error) {
      this.toastr.error('Failed to apply adjustment.');
    }
    this.isSubmitting = false;
    return;

  }
}
